/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Global timeline of updates across all components in the library
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - Scroll through global chronological updates
 * - Filter by component name or date range
 * - Navigate to: ComponentDetail
 *
 * ## Basic Layout
 * Column split: Sidebar + Main
 *
 * ## Reuse Components
 * - `LayoutSidebar`
 *
 * ## Page Layout
 * `flex` Column split: Sidebar + Content `flex-1`
 *
 * **Sidebar**: Reuse `LayoutSidebar`, activeKey="changelog"
 *
 * **Main Content** `flex flex-col h-screen overflow-hidden`:
 * - **Header** `flex items-center justify-between`: Title "Changelog"
 * - **Filters** `flex`: Component Select + Date Range Picker
 * - **Timeline Area** `flex-1 overflow-auto flex flex-col`:
 *   - Timeline container `border-l`:
 *     - Timeline Item `flex flex-col relative`: Date header, followed by card containing Component Name, Version Badge, Avatar, and Markdown changelog notes.
 *
 * ## Mock Data
 * Timeline spanning last 2 weeks. 8 updates across 4 different components. Mixture of major/minor/patch notes.
 *
 * ## Reference
 * - canvas/[Feature] VersionManagement/user-flow.md
 * </page-design>
 */

import { useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as ChangelogStore from '@/stores/ChangelogStore'
import { pageLinks } from '@/pageLinks'

function Filters() {
  const { data } = useAtomValue(ChangelogStore.refreshAtom)
  const filterComponent = useAtomValue(ChangelogStore.filterComponentAtom)
  const setFilterComponent = useSetAtom(ChangelogStore.filterComponentAtom)

  return (
    <div className="flex items-center gap-4 mb-6 px-2">
      <select value={filterComponent} onChange={e => setFilterComponent(e.target.value)} className="px-4 py-2.5 bg-white rounded-xl text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 transition-all text-gray-700">
        <option value="">全部组件</option>
        {data?.components.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </div>
  )
}

function Timeline() {
  const { data, isPending } = useAtomValue(ChangelogStore.refreshAtom)
  const filterComponent = useAtomValue(ChangelogStore.filterComponentAtom)

  const filtered = useMemo(() => {
    if (!data) return []
    let entries = data.entries
    if (filterComponent) entries = entries.filter(e => e.componentId === filterComponent)
    return entries
  }, [data, filterComponent])

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>()
    for (const entry of filtered) {
      const date = entry.createdAt ? new Date(entry.createdAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }) : '未知日期'
      if (!map.has(date)) map.set(date, [])
      map.get(date)!.push(entry)
    }
    return Array.from(map.entries())
  }, [filtered])

  if (isPending) {
    return (
      <div className="animate-pulse space-y-6 px-2">
        {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white rounded-2xl" />)}
      </div>
    )
  }

  if (filtered.length === 0) {
    return <div className="text-center py-20 text-gray-400"><Icon icon="lucide:file-clock" className="text-5xl mb-4 mx-auto" /><p>暂无变更记录</p></div>
  }

  return (
    <div className="relative ml-6 px-2">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200" />
      {grouped.map(([date, entries]) => (
        <div key={date} className="mb-8">
          <div className="relative flex items-center mb-4 -ml-6">
            <div className="w-3 h-3 rounded-full bg-[#1D263A] ring-4 ring-[#F8F9FA] shrink-0" />
            <span className="ml-4 text-sm font-bold text-gray-700">{date}</span>
          </div>
          <div className="space-y-3 pl-4">
            {entries.map(entry => (
              <Link key={entry.id} to={pageLinks.ComponentDetail(entry.componentId)} className="block bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-semibold">{entry.componentName}</span>
                  <span className="px-2.5 py-0.5 bg-[#1D263A]/5 text-[#1D263A] text-xs font-mono font-medium rounded-lg">v{entry.version}</span>
                  {entry.publishedBy && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">{entry.publishedBy.charAt(0).toUpperCase()}</span>
                      {entry.publishedBy}
                    </span>
                  )}
                </div>
                {entry.changelogNotes && <p className="text-sm text-gray-600 leading-relaxed">{entry.changelogNotes}</p>}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Changelog() {
  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        <header className="flex items-center justify-between mb-6 px-2">
          <h1 className="text-2xl font-bold tracking-tight">变更日志</h1>
        </header>
        <Filters />
        <div className="flex-1 overflow-y-auto pb-8">
          <Timeline />
        </div>
      </div>
    </div>
  )
}
